<script>
	import { user, view, send_data, change_attribute } from "../client.svelte.js";
	import {storageCats, perksToString} from "../core.js";
  import EquipmentDialog from "../dialogs/EquipmentDialog.svelte";
	import Sheet from "./Sheet.svelte";
	import Sidebar from "../elements/Sidebar.svelte";
	import {note, choice} from "./SheetFields.svelte";

	let { editable = false, tableOnly = false } = $props()
	let disabled = $derived(!editable)


	const excludedKeys = ['ID', 'TYPE', 'quality', 'charID', 'received', 'cat']
	const filterGearItem = (gearitem => (!gearitem) ? {} :
		Object.fromEntries(
			Object.entries(gearitem)
				.filter(([key,val]) => !excludedKeys.includes(key))))

	let gold = $derived(user.possessions.get(`${view.character?.ID}$gold`)?.amount ?? 0)
	let silver = $derived(user.possessions.get(`${view.character?.ID}$silver`)?.amount ?? 0)
	let grpItems = $derived(
		Object.groupBy(
			view.character.possessions
				.map(possID => user.possessions.get(possID))
				.filter(poss => poss && poss.gained !== null)		// without items only marked for trade
				.map(poss => Object.assign({}, poss, filterGearItem(user.gearitems.get(poss.ITEM))))
				.filter(poss => poss.name && !(poss.ITEM === 'gold' || poss.ITEM === 'silver'))
				.sort((a,b) => a.name.localeCompare(b.name))
			, (poss) => poss.stored)
	)
	// $inspect(`GEARSHEET - possessions`, grpItems)

	let newStore = $state("")
	let storages = $derived(
		Object.keys(storageCats)
			.concat(Object.keys(grpItems).filter(grp => !(grp in storageCats)))
			.filter(stored => grpItems[stored]?.length > 0)
			.concat((newStore !== "") ? [newStore] : [])
	)
	let unuseds = $derived(
		Object.entries(storageCats)
			.filter(([abbrev, name]) => !storages.includes(abbrev))
			.map(([abbrev, name]) => ({ID: abbrev, name: name}))
	)

	let dragID = $state(null)		// possession currently dragged
	let dropOver = $state(null)		// storage currently hovered while dragging

	/** splits "Waffen (möglichst immer in Reichweite)" into title and note **/
	function store_label(store) {
		const [, title, note] = (storageCats[store] ?? store).match(/^(.*?)\s*(\(.*\))?$/)
		return {title: title || "Ohne Behältnis", note: note ?? ""}
	}

	function weight_label(weight) {
		return (weight || weight === 0) ? `${weight.toLocaleString('de', {minimumFractionDigits: 1})} kg` : ""
	}

	function edit_item(e) {
		if (['INPUT', 'TEXTAREA'].includes(e.target.tagName))  return
		view.show_dialog('gearitem', {item: this, mode: 'view'})
		console.log(`GEARSHEET - this item`, this)
	}

	function pickup_item(e) {
		view.dragParams = {typ: 'item', ID: this.ID}
		dragID = this.ID
		e.dataTransfer.setData('item/ID', this.ID)
		e.dataTransfer.effectAllowed = 'move'
		e.dataTransfer.setDragImage(e.target, 25, 25)
	}

	function dragging_item(e) {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		dropOver = this
	}

	function leave_store(e) {
		if (!e.currentTarget.contains(e.relatedTarget))
			dropOver = null
	}

	function end_drag() {
		dragID = null
		dropOver = null
	}

	function reorg_item(e) {
		e.preventDefault();
		end_drag()
		const posses = user.possessions.get(e.dataTransfer.getData('item/ID'))
		if (!posses || posses.stored === this)  return
		posses.stored = this
		send_data(`possession/${posses.ID}`, {stored: this}, 'PATCH')
		if (this === newStore)
			newStore = ""
	}
</script>


<!-------------------------------- HTML -------------------------------------->

<section id='gear'>

	<!-- sits in the navigation bar, top right -->
	<Sidebar items={[
		{id: 'equipment', icon: 'person', label: "Ausrüstung", title: "Ausrüstung anlegen und ablegen", primary: true,
		 onclick: () => document.getElementById('equip-dialog')?.showModal()} ]}/>

	<Sheet>
		{#snippet stat()}
			<div class="coins" data-help="coins">
				<label class="kicker">Gold</label>
				<span class="num">{gold}</span>
				<label class="kicker">Silber</label>
				<span class="num">{silver}</span>
			</div>
		{/snippet}

		{#each storages as store}
			{@const label = store_label(store)}
			<section class="storage" data-help="storage" class:dropping={dropOver === store} role="list"
			         ondragover={dragging_item.bind(store)} ondragleave={leave_store} ondrop={reorg_item.bind(store)}>
				<div class="storage-head">
					<h3 class="storage-title">{label.title}</h3>
					<span class="muted">{label.note}</span>
				</div>
				<div class="gear-row kicker colhead">
					<span class="center">Anz</span>
					<span>Gegenstand</span>
					<span>Beschreibung</span>
					<span class="center">Qual</span>
					<span>Besonderheiten</span>
					<span class="right" title="Gewicht in kg">Gew.</span>
					<span>Bemerkungen</span>
				</div>
				{#each grpItems[store] ?? [] as item}
					<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
					<div class="gear-row" data-help="item" class:dragged={dragID === item.ID} role="listitem" onclick={edit_item.bind(item)}>
						<span class="center num">{item.amount ?? 1}</span>
						<span class="item-name" draggable="true" role="button" tabindex="-1" title={item.ID}
						      ondragstart={pickup_item.bind(item)} ondragend={end_drag}>
							{item.name}
						</span>
						<span class="descript">{item.descript ?? ""}</span>
						<span class="center grade num">{(item.grade > 0) ? item.grade : ""}</span>
						<span class="specials" class:greyed={!item.perks} title={perksToString(item.perks)}>{item.specials ?? ""}</span>
						<span class="right num weight">{weight_label(item.weight)}</span>
						{@render note({value: item.comment, disabled, placeholder: "", onchange: change_attribute.bind(item)})}
					</div>
				{:else}
					<p class="empty">Leer — Gegenstände hierher ziehen.</p>
				{/each}
			</section>
		{/each}

		{#if editable && unuseds.length > 0}
			<div class="add-store" data-help="newStore">
				<span class="kicker">Behältnis hinzufügen</span>
				{@render choice({id: 'newStore', options: unuseds, onchange: (e) => newStore = e.target.value})}
			</div>
		{/if}
	</Sheet>

	<EquipmentDialog/>

</section>



<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	.intro
		margin: -10px 0 24px

	.storage
		margin: 0 -14px 30px
		padding: 10px 14px 6px
		border: 1px dashed transparent
		border-radius: var(--radius-md)

		&.dropping
			border-color: var(--color-accent)

	.storage-head
		display: flex
		align-items: baseline
		flex-wrap: wrap
		gap: 12px
		margin-bottom: 4px

	.storage-title
		font-size: 24px
		margin: 1.5em 0 8px

	.gear-row
		display: grid
		grid-template-columns: 4ch 15ch 2fr 2ch 2fr 5ch 1fr
		column-gap: 14px
		align-items: baseline
		padding: 7px 0
		border-bottom: 1px solid var(--color-divider)
		cursor: pointer

		&.colhead
			padding: 8px 0
			border-bottom-color: var(--color-accent-300)
			letter-spacing: .12em
			cursor: default

		&:not(.colhead):hover
			background: var(--hover-tint)

		&.dragged
			opacity: .4

		:global(.note)
			padding: 0
			font-size: 14px

	.item-name
		display: flex
		align-items: baseline
		gap: 8px
		font-size: 16px
		cursor: grab

		.fa
			font-size: 10px
			color: var(--color-neutral-500)

	.descript
		font-size: 14px
		color: var(--color-neutral-800)

	.grade
		font-family: var(--font-heading)
		font-size: 18px

	.specials
		font-size: 14px
		color: var(--color-accent-800)

		&.greyed
			color: var(--color-neutral-600)

	.weight
		font-size: 14px

	.empty
		margin: 10px 0
		font-size: 14px
		font-style: italic
		color: var(--color-neutral-600)

	.add-store
		margin: 1em 0 0 0
		display: flex
		align-items: center
		gap: 12px

	.coins
		display: grid
		grid-template-columns: 8ch 6ch
		align-items: baseline
		grid-gap: 6px

		span
			font-family: var(--font-heading)
			font-size: 30px
			line-height: 1.1

		.num
			text-align: right

</style>
